import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { formatPrice } from '../utils/currency';
import CustomerPaymentModal from './CustomerPaymentModal';
import LoadingSpinner from './LoadingSpinner';

const CustomerMenu = ({ apiClient }) => {
  const { restaurantId, tableId } = useParams();
  const navigate = useNavigate();
  const [menuData, setMenuData] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Ultra-fast loading states - show skeleton instantly
  const [restaurantLoaded, setRestaurantLoaded] = useState(false);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [allItemsLoaded, setAllItemsLoaded] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [orderLoading, setOrderLoading] = useState(false);
  const [taxBreakdown, setTaxBreakdown] = useState([]);
  const [minimumPaymentWarning, setMinimumPaymentWarning] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    phone: '',
    email: '',
    notes: ''
  });
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentModal, setPaymentModal] = useState({
    show: false,
    clientSecret: null,
    orderDetails: null,
    paymentIntentId: null
  });
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const [menuItemsByCategory, setMenuItemsByCategory] = useState({});
  const [categoryLoading, setCategoryLoading] = useState(false);

  // Helper function to get order capacity from different data structures
  const getOrderCapacity = () => {
    return menuData?.order_capacity ?? menuData?.restaurant?.order_capacity ?? 0;
  };

  // Handle user consent to minimum payment amount
  const proceedWithMinimumPayment = async () => {
    try {
      setOrderLoading(true);
      console.log('🔄 User confirmed minimum payment, creating payment intent...');
      
      const paymentResponse = await apiClient.post(`/orders/create-with-payment-minimum-consent`, {
        restaurant_id: restaurantId,
        table_id: tableId,
        order_amount: minimumPaymentWarning.order_amount,
        minimum_amount: minimumPaymentWarning.minimum_charge_amount,
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        order_data: {
          items: cart.map(item => ({
            menu_item_id: item.menu_item_id,
            quantity: item.quantity,
            notes: item.notes
          })),
          notes: customerInfo.notes
        }
      });
      
      console.log('✅ Minimum payment intent created:', paymentResponse.data);
      
      // Close warning and open payment modal
      setMinimumPaymentWarning(null);
      setPaymentModal({
        isOpen: true,
        clientSecret: paymentResponse.data.client_secret,
        orderDetails: {
          restaurant_name: paymentResponse.data.restaurant_name,
          table_name: paymentResponse.data.table_name,
          order_amount: paymentResponse.data.order_amount,
          charged_amount: paymentResponse.data.charged_amount,
          currency: minimumPaymentWarning.currency
        },
        paymentIntentId: paymentResponse.data.payment_intent_id
      });
      
    } catch (error) {
      console.error('Error creating minimum payment intent:', error);
      toast.error('Failed to setup payment. Please try again.');
    } finally {
      setOrderLoading(false);
    }
  };

  useEffect(() => {
    fetchMenuData();
  }, [restaurantId, tableId]);

  // Set initial selected category when menu data loads (skip empty categories)
  useEffect(() => {
    if (menuData?.categories && menuData.categories.length > 0 && !selectedCategory) {
      // Find first category with items
      const firstNonEmptyCategory = menuData.categories.find(cat => {
        const itemCount = cat.item_count ?? (menuItemsByCategory?.[cat.id]?.length || menuData?.menu_items?.[cat.id]?.length || 0);
        return itemCount > 0;
      });
      
      if (firstNonEmptyCategory) {
        setSelectedCategory(firstNonEmptyCategory.id);
      }
    }
  }, [menuData?.categories, menuItemsByCategory]);

  // Remove the intersection observer since we're now showing one category at a time
  // const observer = new IntersectionObserver(...) - removed

  // Ultra-Fast Single Request Loading
  const loadMenuDataUltraFast = async () => {
    console.log('🚀 ULTRA-FAST LOADING START...');
    const startTime = performance.now();
    
    try {
      setLoadingProgress(10);
      
      // Single request for all data
      const response = await apiClient.get(`/menu/${restaurantId}/${tableId}/ultra-fast`);
      const totalTime = performance.now() - startTime;
      
      const data = response.data;
      
      // Set all data at once with smooth animation
      setMenuData({
        restaurant: data.restaurant,
        table: data.table,
        categories: data.categories,
        tax_components: data.tax_components
      });
      
      // Set menu items grouped by category
      setMenuItemsByCategory(data.menu_items_by_category || {});
      
      // Select first non-empty category
      if (data.categories && data.categories.length > 0) {
        const firstNonEmptyCategory = data.categories.find(cat => {
          const items = data.menu_items_by_category?.[cat.id] || [];
          return items.length > 0;
        });
        
        if (firstNonEmptyCategory) {
          setSelectedCategory(firstNonEmptyCategory.id);
        }
      }
      
      setRestaurantLoaded(true);
      setCategoriesLoaded(true);
      setLoadingProgress(100);
      
      console.log(`🚀 Ultra-fast loading completed in ${totalTime.toFixed(1)}ms`);
      
    } catch (error) {
      console.error('❌ Ultra-fast loading failed:', error);
      // Fallback to progressive loading
      await loadMenuDataProgressively();
    }
  };

  // Progressive Loading Functions - True Breeze Speed (Fallback)
  // True progressive loading - loads everything smoothly in background
  const loadMenuDataProgressively = async () => {
    console.log('🌪️ SMOOTH PROGRESSIVE LOADING START...');
    const overallStart = performance.now();
    
    try {
      // Phase 1: Load restaurant basics (instant UI update)
      setLoadingProgress(10);
      const startBasics = performance.now();
      const basicsResponse = await apiClient.get(`/menu/${restaurantId}/${tableId}/basic`);
      const basicsTime = performance.now() - startBasics;
      
      // Check for error in response
      if (basicsResponse.data.error) {
        setMenuData({ error: basicsResponse.data.error });
        return;
      }
      
      // Immediately update UI with restaurant info
      setMenuData(basicsResponse.data);
      setRestaurantLoaded(true);
      setLoadingProgress(25);
      console.log(`⚡ Phase 1: Restaurant basics loaded in ${basicsTime.toFixed(1)}ms`);
      
      // Phase 2: Load categories (instant navigation ready)
      const startCategories = performance.now();
      const categoriesResponse = await apiClient.get(`/menu/${restaurantId}/categories`);
      const categoriesTime = performance.now() - startCategories;
      
      const categories = categoriesResponse.data.categories || [];
      
      // Update UI with categories immediately
      setMenuData(prev => ({
        ...prev,
        categories: categories
      }));
      setCategoriesLoaded(true);
      setLoadingProgress(40);
      console.log(`⚡ Phase 2: Categories loaded in ${categoriesTime.toFixed(1)}ms`);
      
      // Phase 3: Load ALL menu items for ALL categories in parallel (smooth background loading)
      if (categories.length > 0) {
        // We'll select the first category with items after loading, initially set to first category
        setSelectedCategory(categories[0].id);
        setLoadingProgress(50);
        
        const startItems = performance.now();
        
        // Load all category items in parallel for instant category switching
        const itemPromises = categories.map(async (category, index) => {
          try {
            const response = await apiClient.get(`/menu/${restaurantId}/category/${category.id}/items`);
            
            // Update progress as each category loads
            setLoadingProgress(50 + ((index + 1) / categories.length) * 45);
            
            return {
              categoryId: category.id,
              items: response.data.menu_items || [],
              taxComponents: response.data.tax_components
            };
          } catch (error) {
            console.log(`⚠️ Failed to load items for category ${category.name}, using empty array`);
            return {
              categoryId: category.id,
              items: [],
              taxComponents: null
            };
          }
        });
        
        // Wait for all category items to load
        const allCategoryItems = await Promise.all(itemPromises);
        const itemsTime = performance.now() - startItems;
        
        // Build complete menu items object and get tax components from first response
        const menuItemsMap = {};
        let taxComponents = null;
        
        allCategoryItems.forEach(({ categoryId, items, taxComponents: categoryTaxComponents }) => {
          menuItemsMap[categoryId] = items;
          // Use tax components from first category (they should be the same for all)
          if (!taxComponents && categoryTaxComponents) {
            taxComponents = categoryTaxComponents;
          }
        });
        
        // Final update with all menu items and tax components
        setMenuData(prev => ({
          ...prev,
          menu_items: menuItemsMap,
          tax_components: taxComponents || prev.tax_components
        }));
        
        // Update menuItemsByCategory state
        setMenuItemsByCategory(menuItemsMap);
        
        // After loading items, select first non-empty category
        const firstNonEmptyCategory = categories.find(cat => {
          const items = menuItemsMap[cat.id] || [];
          return items.length > 0;
        });
        
        if (firstNonEmptyCategory) {
          setSelectedCategory(firstNonEmptyCategory.id);
        }
        
        setAllItemsLoaded(true);
        setLoadingProgress(100);
        
        const totalTime = performance.now() - overallStart;
        console.log(`🎉 SMOOTH PROGRESSIVE LOADING COMPLETE in ${totalTime.toFixed(1)}ms`);
        console.log(`   📊 Performance: Basics(${basicsTime.toFixed(1)}ms) + Categories(${categoriesTime.toFixed(1)}ms) + Items(${itemsTime.toFixed(1)}ms)`);
        
      }
      
    } catch (error) {
      console.error('Progressive loading failed:', error);
      // Graceful fallback - still show what we have
      setRestaurantLoaded(true);
      setCategoriesLoaded(true);
      setAllItemsLoaded(true);
      setLoadingProgress(100);
    }
  };

  // Main progressive loading orchestrator
  const fetchMenuData = async () => {
    try {
      // Try ultra-fast loading first
      await loadMenuDataUltraFast();
    } catch (error) {
      console.error('Menu loading failed:', error);
      setMenuData({ error: 'Failed to load menu. Please check your QR code.' });
    } finally {
      setLoading(false);
    }
  };

  const getSelectedQuantity = (itemId) => {
    return selectedQuantities[itemId] || 1;
  };

  const updateSelectedQuantity = (itemId, quantity) => {
    setSelectedQuantities(prev => ({
      ...prev,
      [itemId]: Math.max(1, quantity)
    }));
  };

  const addToCart = (menuItem, categoryId, quantity = null) => {
    const actualQuantity = quantity || getSelectedQuantity(menuItem.id);
    const existingItem = cart.find(item => item.menu_item_id === menuItem.id);
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.menu_item_id === menuItem.id 
          ? { ...item, quantity: item.quantity + actualQuantity }
          : item
      ));
    } else {
      setCart([...cart, {
        menu_item_id: menuItem.id,
        menu_item_name: menuItem.name,
        price: menuItem.price,
        quantity: actualQuantity,
        notes: ''
      }]);
    }
    
    toast.success(`${actualQuantity}x ${menuItem.name} added to cart`);
  };

  const updateCartItemQuantity = (menuItemId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(menuItemId);
    } else {
      setCart(cart.map(item => 
        item.menu_item_id === menuItemId 
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const removeFromCart = (menuItemId) => {
    setCart(cart.filter(item => item.menu_item_id !== menuItemId));
  };

  const updateCartItemNotes = (menuItemId, notes) => {
    // Limit notes to 70 characters
    const limitedNotes = notes.slice(0, 70);
    
    // Show toast notification when limit is reached
    if (notes.length > 70) {
      toast.warning(`Character limit reached! Item notes limited to 70 characters.`, {
        duration: 3000,
        position: 'top-center'
      });
    } else if (notes.length >= 65 && notes.length <= 70) {
      // Warning when approaching limit
      toast.info(`${70 - notes.length} characters remaining for item notes`, {
        duration: 1500,
        position: 'top-center'
      });
    }
    
    setCart(cart.map(item => 
      item.menu_item_id === menuItemId 
        ? { ...item, notes: limitedNotes }
        : item
    ));
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateTaxes = (subtotal) => {
    if (!menuData?.tax_components) return { taxes: [], totalTax: 0 };
    
    const taxes = menuData.tax_components.map(tax => {
      let amount = 0;
      if (tax.type === 'percentage') {
        amount = (subtotal * tax.value) / 100;
      } else {
        amount = tax.value;
      }
      
      return {
        name: tax.name,
        type: tax.type,
        value: tax.value,
        amount: amount
      };
    });
    
    const totalTax = taxes.reduce((sum, tax) => sum + tax.amount, 0);
    
    return { taxes, totalTax };
  };

  const getFinalTotal = () => {
    const subtotal = getTotalAmount();
    const { totalTax } = calculateTaxes(subtotal);
    return subtotal + totalTax;
  };

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    // Limit notes field to 70 characters
    const limitedValue = name === 'notes' ? value.slice(0, 70) : value;
    
    // Show toast notification when limit is reached for notes field
    if (name === 'notes') {
      if (value.length > 70) {
        toast.warning(`Character limit reached! Order notes limited to 70 characters.`, {
          duration: 3000,
          position: 'top-center'
        });
      } else if (value.length >= 65 && value.length <= 70) {
        // Warning when approaching limit
        toast.info(`${70 - value.length} characters remaining for order notes`, {
          duration: 1500,
          position: 'top-center'
        });
      }
    }
    
    setCustomerInfo({
      ...customerInfo,
      [name]: limitedValue
    });
  };

  const submitOrder = async (paymentMethod) => {
    if (cart.length === 0) {
      toast.error('Please add items to your cart');
      return;
    }

    // Customer information is optional - no validation needed
    // Users can place orders without providing name or phone as shown in UI

    const finalTotal = getFinalTotal();
    const currency = menuData?.restaurant?.currency || 'USD';
    
    // If order total is negative, place order directly without payment
    if (finalTotal < 0) {
      console.log('🎯 Negative total detected, placing order directly without payment');
      toast.info('Order total is negative - placing order without payment required!');
      
      // Force "pay_later" method for negative totals
      paymentMethod = 'pay_later';
    }
    
    // Check minimum amount for card payments (only for positive totals)
    if (finalTotal >= 0) {
      if (paymentMethod === 'card' && currency.toLowerCase() === 'inr' && finalTotal < 50) {
        toast.warning('Minimum ₹50 required for card payments. Please add more items or choose "Pay Later".');
        return;
      } else if (paymentMethod === 'card' && finalTotal < 1) {
        toast.warning('Order amount too small for card payment. Please add more items or choose "Pay Later".');
        return;
      }
    }

    setOrderLoading(true);
    
    try {
      const orderData = {
        table_id: tableId,
        items: cart.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          notes: item.notes
        })),
        customer_name: customerInfo.name,
        customer_phone: customerInfo.phone,
        customer_email: customerInfo.email,
        payment_method: paymentMethod,
        notes: customerInfo.notes
      };

      if (paymentMethod === 'stripe') {
        console.log('🔄 Card payment selected, creating payment intent without order...');
        console.log('🔄 Restaurant ID:', restaurantId);
        console.log('🔄 Customer info:', customerInfo);
        
        // Create payment intent WITHOUT creating order first
        try {
          console.log('🔄 Calling payment-first API...');
          const paymentResponse = await apiClient.post(`/orders/create-with-payment`, {
            table_id: tableId,
            items: cart.map(item => ({
              menu_item_id: item.menu_item_id,
              quantity: item.quantity,
              notes: item.notes
            })),
            customer_name: customerInfo.name,
            customer_phone: customerInfo.phone,
            customer_email: customerInfo.email,
            notes: customerInfo.notes
          });
          
          console.log('✅ Payment response received:', paymentResponse.data);
          
          // Check if minimum payment warning is required
          if (paymentResponse.data.requires_minimum_warning) {
            console.log('⚠️ Minimum payment warning required');
            setMinimumPaymentWarning(paymentResponse.data);
            setOrderLoading(false);
            return; // Don't proceed to payment yet, show warning first
          }
          
          // Validate response data for normal payment flow
          if (!paymentResponse.data.client_secret) {
            throw new Error('No client_secret in payment response');
          }
          
          console.log('🔄 Opening payment modal...');
          setPaymentModal({
            show: true,
            clientSecret: paymentResponse.data.client_secret,
            paymentIntentId: paymentResponse.data.payment_intent_id,
            orderDetails: {
              total: paymentResponse.data.total_amount,
              restaurant: paymentResponse.data.restaurant_name,
              table: paymentResponse.data.table_name,
              items: cart.map(item => `${item.quantity}x ${item.menu_item_name}`).join(', '),
              customer: customerInfo,
              cart: cart,  // Pass cart for "pay later" option
              currency: menuData?.restaurant?.currency || 'USD'
            },
            hasOrderDetails: !!paymentResponse.data.restaurant_name
          });
          
          // IMPORTANT: Exit here for card payments - don't show success message yet
          console.log('✅ EXITING for card payment - modal should handle success');
          return;
          
        } catch (paymentError) {
          console.error('❌ Payment intent creation FAILED:', paymentError);
          console.error('❌ Error details:', paymentError.response?.data);
          console.error('❌ Error status:', paymentError.response?.status);
          
          // Handle specific error cases
          let errorMessage = 'Payment setup failed. Please try again.';
          if (paymentError.response?.status === 400) {
            const errorDetail = paymentError.response?.data?.detail || '';
            
            // Check if it's a minimum amount error
            if (errorDetail.includes('below minimum charge requirement')) {
              // Show specific error for minimum amount with alternative payment options
              errorMessage = errorDetail;
              toast.error(errorMessage, {
                duration: 8000 // Show longer for minimum amount errors
              });
              
              // Also suggest alternative payment methods
              setTimeout(() => {
                toast.info("💡 Try paying with Cash or add more items to meet the minimum amount for card payments.", {
                  duration: 6000
                });
              }, 500);
              
            } else {
              errorMessage = errorDetail || 'Unable to process payment. Please check with restaurant.';
              toast.error(errorMessage);
            }
          } else if (paymentError.response?.status === 404) {
            errorMessage = 'Order not found. Please try placing the order again.';
            toast.error(errorMessage);
          } else if (paymentError.response?.data?.detail) {
            errorMessage = paymentError.response.data.detail;
            toast.error(errorMessage);
          } else {
            toast.error(errorMessage);
          }
          
          console.log('❌ EXITING after payment error - no success message');
          return;
        }
      }
      
      // For other payment methods (cash, pay_later) - create order first, then show success
      console.log('✅ Non-card payment selected, creating order with ' + paymentMethod + ' payment method');
      
      const response = await apiClient.post('/orders', orderData);
      
      console.log('✅ Non-card payment order created successfully:', response.data);
      toast.success('Order placed successfully!');
      setCart([]);
      setShowCart(false);
      setShowCheckout(false);
      
      // Show order confirmation toast
      toast.success(
        `Order #${response.data.id.slice(-8)} placed successfully!\n\nTotal: ${formatPrice(response.data.total_amount, menuData?.restaurant?.currency || 'USD')}\nPayment Method: ${paymentMethod}\n\nThank you for your order!`,
        {
          duration: 10000 // 10 seconds
        }
      );
      
    } catch (orderError) {
      console.error('❌ Order creation FAILED:', orderError);
      console.error('❌ Error details:', orderError.response?.data);
      console.error('❌ Error status:', orderError.response?.status);
      
      // Handle specific order creation errors
      let errorMessage = 'Failed to place order. Please try again.';
      if (orderError.response?.status === 400) {
        const detail = orderError.response?.data?.detail || '';
        if (detail.includes('not accepting orders') || detail.includes('order capacity')) {
          errorMessage = 'Restaurant is not accepting orders at the moment. Please try again later.';
        } else {
          errorMessage = detail || 'Unable to place order. Please check your order details.';
        }
      } else if (orderError.response?.status === 404) {
        errorMessage = 'Restaurant or table not found. Please check your QR code.';
      } else if (orderError.response?.data?.detail) {
        errorMessage = orderError.response.data.detail;
      }
      
      toast.error(errorMessage);
      console.log('❌ Order creation failed, no success message will be shown');
    } finally {
      setOrderLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      // Confirm payment using the optimized endpoint with restaurant_id
      const response = await apiClient.post(`/orders/confirm-payment/${restaurantId}/${paymentIntentId}`);
      
      toast.success(`Payment successful! Order #${response.data.order_id.slice(-8)} confirmed.`);
      setCart([]);
      setShowCart(false);
      setShowCheckout(false);
      setPaymentModal({ show: false, clientSecret: null, orderDetails: null, paymentIntentId: null });
      
      // Show order confirmation toast
      toast.success(
        `Order #${response.data.order_id.slice(-8)} paid successfully!\n\nTotal: ${formatPrice(response.data.amount_paid, menuData?.restaurant?.currency || 'USD')}\nPayment Method: Card\n\nThank you for your order!`,
        {
          duration: 10000 // 10 seconds
        }
      );
    } catch (error) {
      console.error('Error confirming payment:', error);
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      
      if (errorMessage.includes('Payment not completed')) {
        toast.error('Payment was not completed. Please try again or contact support.');
      } else if (errorMessage.includes('minimum') || errorMessage.includes('convert')) {
        toast.warning('Payment amount issue. Please add more items or choose "Pay Later".');
      } else {
        toast.error(`Payment confirmation failed: ${errorMessage}`);
      }
    }
  };

  const handlePaymentCancel = async () => {
    try {
      // Cancel the payment intent and create a "pay later" order using optimized endpoint
      const response = await apiClient.post(`/orders/${restaurantId}/cancel-payment-and-create`, {
        payment_intent_id: paymentModal.paymentIntentId,
        table_id: tableId,
        items: paymentModal.orderDetails.cart.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity,
          notes: item.notes || ''
        })),
        customer_name: paymentModal.orderDetails.customer.name,
        customer_phone: paymentModal.orderDetails.customer.phone,
        notes: paymentModal.orderDetails.customer.notes
      });
      
      // Clear cart and close modal
      setCart([]);
      setCustomerInfo({ name: '', phone: '', email: '', notes: '' });
      setShowCheckout(false);
      setPaymentModal({ show: false, clientSecret: null, orderDetails: null, paymentIntentId: null });
      
      // Show success message for pay later order
      toast.success(
        `Order #${response.data.order_id.slice(-8)} placed successfully!\n\nTotal: ${formatPrice(response.data.total_amount, menuData?.restaurant?.currency || 'USD')}\nPayment Method: Pay Later\n\nThank you for your order!`,
        {
          duration: 10000 // 10 seconds
        }
      );
      
    } catch (error) {
      console.error('Error creating pay later order:', error);
      
      // Handle specific error messages for pay later orders
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      
      if (errorMessage.includes('capacity')) {
        toast.error('Restaurant has reached order capacity. Please try again later.');
      } else if (errorMessage.includes('not found')) {
        toast.error('Payment or order information not found. Please try placing a new order.');
      } else if (error.response?.status === 400) {
        // Show the specific validation error message from backend
        toast.error(errorMessage);
      } else if (error.response?.status === 401) {
        toast.error('Session expired. Please refresh the page and try again.');
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again or contact the restaurant.');
      } else {
        toast.error(`Pay later order failed: ${errorMessage}`);
      }
    }
  };
  const selectCategory = (categoryId) => {
    // Show loading indicator briefly when switching categories
    setCategoryLoading(true);
    setSelectedCategory(categoryId);
    
    // All items are now loaded upfront, no need for lazy loading
    // Hide loading indicator after a brief moment
    setTimeout(() => {
      setCategoryLoading(false);
    }, 150);
    
    // Scroll to show first menu item in full visibility after category switch
    setTimeout(() => {
      const categorySection = document.querySelector(`[data-testid="category-section-${categoryId}"]`);
      if (categorySection) {
        // Get the header height to account for sticky navigation
        const header = document.querySelector('header');
        const headerHeight = header ? header.offsetHeight : 0;
        
        // Calculate position to show first item in full visibility
        const scrollPosition = categorySection.offsetTop - headerHeight - 10; // 10px padding
        
        window.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }, 100); // Small delay to ensure DOM updates
  };

  // BREEZE LOADING: No loading screen - show skeleton in-place instantly
  const showSkeleton = !restaurantLoaded || !menuData;

  // Show full-screen loading spinner for initial page load (before any data)
  if (loading && !menuData) {
    return <LoadingSpinner fullScreen={true} message="Loading menu..." size="large" />;
  }

  // Show error only if we have a real error, not loading states
  if (menuData?.error && loadingProgress === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-4">Unable to load menu</h1>
          <p className="text-gray-600">{menuData.error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Mobile-First Responsive */}
      <header className="bg-white shadow-lg sticky top-0 z-40 border-b">
        {/* Restaurant Info Section */}
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-3 sm:py-4">
          {/* Mobile Layout - Centered */}
          <div className="sm:hidden">
            <div className="text-center mb-3">
              {showSkeleton ? (
                <div className="animate-pulse">
                  <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-40 mx-auto mb-2"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-32 mx-auto"></div>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl font-bold bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 bg-clip-text text-transparent drop-shadow-sm" data-testid="restaurant-name-display">
                    {menuData.restaurant.name}
                  </h1>
                  <div className="flex items-center justify-center mt-2">
                    <div className="bg-gray-100 px-3 py-1 rounded-full border border-gray-200">
                      <p className="text-sm font-medium text-gray-700" data-testid="table-name-display">
                        Table : {menuData.table.table_number}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {cart.length > 0 && (
              <button
                onClick={() => setShowCart(true)}
                data-testid="view-cart-button"
                className="relative bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors duration-200 font-medium text-base shadow-md flex items-center justify-center min-h-[48px] w-full"
              >
                <span className="mr-2">🛒</span>
                View Order ({cart.reduce((total, item) => total + item.quantity, 0)})
                <span 
                  className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1 shadow-lg"
                  data-testid="cart-item-count"
                >
                  {cart.reduce((total, item) => total + item.quantity, 0)}
                </span>
              </button>
            )}
          </div>

          {/* Desktop Layout - Horizontal */}
          <div className="hidden sm:flex justify-between items-center">
            <div className="flex-1 min-w-0">
              {showSkeleton ? (
                <div className="animate-pulse">
                  <div className="h-7 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-32 mb-1"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-24"></div>
                </div>
              ) : (
                <>
                  <h1 className="text-2xl lg:text-3xl font-bold bg-gradient-to-r from-amber-700 via-amber-800 to-amber-900 bg-clip-text text-transparent drop-shadow-sm truncate" data-testid="restaurant-name-display-desktop">
                    {menuData.restaurant.name}
                  </h1>
                  <div className="flex items-center mt-1">
                    <div className="bg-gray-100 px-3 py-1 rounded-full border border-gray-200">
                      <p className="text-sm font-medium text-gray-700" data-testid="table-name-display-desktop">
                        Table : {menuData.table.table_number}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {cart.length > 0 && (
              <button
                onClick={() => setShowCart(true)}
                data-testid="view-cart-button-desktop"
                className="relative bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors duration-200 font-medium text-base shadow-md flex items-center justify-center min-h-[44px]"
              >
                <span className="mr-2">🛒</span>
                View Order
                <span 
                  className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-w-[24px] h-6 flex items-center justify-center px-1 shadow-lg"
                  data-testid="cart-item-count-desktop"
                >
                  {cart.reduce((total, item) => total + item.quantity, 0)}
                </span>
              </button>
            )}
          </div>
          
          {(restaurantLoaded && categoriesLoaded && getOrderCapacity() <= 0) && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm font-medium flex items-center">
                <span className="mr-2">⚠️</span>
                This restaurant is not currently accepting new orders.
              </p>
            </div>
          )}
        </div>

        {/* Category Navigation - BREEZE: Show instantly */}
        <div className="border-t border-gray-200 bg-gray-50 overflow-hidden">
          <div className="max-w-4xl mx-auto">
            <div className="flex overflow-x-auto scrollbar-hide px-4 py-3 gap-3">
              {showSkeleton ? (
                // Instant skeleton categories (0ms) with shimmer
                [...Array(6)].map((_, i) => (
                  <div 
                    key={`skel-${i}`} 
                    className="animate-pulse"
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className="flex-shrink-0 h-12 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded-full"></div>
                  </div>
                ))
              ) : (
                // Real categories (after data loads) - filter out empty categories
                (menuData?.categories || [])
                  .filter((category) => {
                    // Use server-provided item count or fallback to loaded items count
                    const itemCount = category.item_count ?? (menuItemsByCategory?.[category.id]?.length || menuData?.menu_items?.[category.id]?.length || 0);
                    // Only show categories with items
                    return itemCount > 0;
                  })
                  .map((category) => {
                    // Use server-provided item count or fallback to loaded items count
                    const itemCount = category.item_count ?? (menuItemsByCategory?.[category.id]?.length || menuData?.menu_items?.[category.id]?.length || 0);
                  
                  return (
                    <button
                      key={category.id}
                      onClick={() => selectCategory(category.id)}
                      className={`flex-shrink-0 px-6 py-3 rounded-full text-sm font-semibold min-w-fit transition-colors duration-200 ${
                        selectedCategory === category.id 
                          ? 'bg-blue-600 text-white shadow-lg' 
                          : 'bg-white text-gray-700 hover:bg-blue-50 border-2 border-gray-200 hover:border-blue-300'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      {category.name} ({itemCount})
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - Mobile-First Responsive */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 pb-20 sm:pb-6" data-testid="menu-content">
        {(!restaurantLoaded || !categoriesLoaded || getOrderCapacity() > 0) ? (
          <div className="space-y-6 sm:space-y-8">
            {/* BREEZE: Show skeleton or content with smooth animations */}
            {showSkeleton ? (
              // Instant skeleton menu items (0ms) with breathing animation
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in">
                <div className="p-4 sm:p-6 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
                  <div className="animate-pulse">
                    <div className="h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-40 mb-2"></div>
                    <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-64"></div>
                  </div>
                </div>
                <div className="p-4 sm:p-6 space-y-4">
                  {[...Array(4)].map((_, i) => (
                    <div 
                      key={`menu-skel-${i}`} 
                      className="animate-pulse border border-gray-200 rounded-lg p-4 transform transition-all duration-300"
                      style={{ animationDelay: `${i * 100}ms` }}
                    >
                      <div className="flex space-x-4">
                        <div className="w-16 h-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded-lg flex-shrink-0"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-3/4"></div>
                          <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-full"></div>
                          <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-1/4"></div>
                        </div>
                        <div className="flex flex-col space-y-2">
                          <div className="w-20 h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded"></div>
                          <div className="w-24 h-10 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (() => {
              const selectedCategoryData = (menuData?.categories || []).find(cat => cat.id === selectedCategory);
              if (!selectedCategoryData) return null;
              
              const categoryItems = menuItemsByCategory?.[selectedCategoryData.id] || menuData?.menu_items?.[selectedCategoryData.id] || [];
              
              // Show loading spinner when switching categories
              if (categoryLoading) {
                return (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in">
                    <LoadingSpinner message="Loading items..." size="medium" />
                  </div>
                );
              }
              
              // Show skeleton loading only if we're still loading AND category should have items
              const expectedItemCount = selectedCategoryData.item_count || 0;
              const isActuallyLoading = !allItemsLoaded && expectedItemCount > 0;
              
              if (categoryItems.length === 0 && isActuallyLoading) {
                return (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in">
                    <div className="p-4 sm:p-6 space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div 
                          key={`loading-item-${i}`} 
                          className="animate-pulse border border-gray-200 rounded-lg p-4"
                          style={{ animationDelay: `${i * 100}ms` }}
                        >
                          <div className="flex space-x-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded-lg flex-shrink-0"></div>
                            <div className="flex-1 space-y-2">
                              <div className="h-5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-3/4"></div>
                              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-full"></div>
                              <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded w-1/4"></div>
                            </div>
                            <div className="flex flex-col space-y-2">
                              <div className="w-20 h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded"></div>
                              <div className="w-24 h-10 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-size-200 animate-shimmer rounded"></div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              
              // Show empty state only if loading is complete and truly no items
              if (categoryItems.length === 0 && allItemsLoaded) {
                return (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center">
                    <p className="text-gray-500">No items available in this category.</p>
                  </div>
                );
              }
              
              return (
                <div 
                  key={selectedCategoryData.id} 
                  id={`category-${selectedCategoryData.id}`}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-fade-in" 
                  data-testid={`category-section-${selectedCategoryData.id}`}
                >
                  
                  <div className="p-3 sm:p-6 space-y-3 sm:space-y-4">
                    {categoryItems.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <div className="text-4xl mb-4">🍽️</div>
                        <p className="text-lg font-medium">No items available</p>
                        <p className="text-sm mt-1">This category doesn't have any menu items yet.</p>
                      </div>
                    ) : (
                      categoryItems.map((item, index) => (
                      <div 
                        key={item.id} 
                        className="bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow duration-200 overflow-hidden animate-fade-in"
                        style={{ animationDelay: `${index * 50}ms` }}
                        data-testid={`menu-item-${item.id}`}
                      >
                        {/* Mobile Layout - Stacked */}
                        <div className="sm:hidden p-4 space-y-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0 pr-3">
                              <h3 className="font-semibold text-gray-900 text-base leading-tight" data-testid={`menu-item-name-${item.id}`}>
                                {item.name}
                              </h3>
                              <p className="text-lg font-bold text-blue-600 mt-1" data-testid={`menu-item-price-${item.id}`}>
                                {formatPrice(item.price, menuData?.restaurant?.currency || 'USD')}
                              </p>
                            </div>
                            {item.image_url && (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                                alt={item.name}
                                className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                data-testid={`menu-item-image-${item.id}`}
                              />
                            )}
                          </div>
                          
                          {item.description && (
                            <p className="text-sm text-gray-600 leading-relaxed" data-testid={`menu-item-description-${item.id}`}>
                              {item.description}
                            </p>
                          )}
                          
                          {/* Quantity Selector */}
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700">Quantity:</span>
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateSelectedQuantity(item.id, getSelectedQuantity(item.id) - 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-medium"
                              >
                                -
                              </button>
                              <span className="w-12 text-center font-medium text-lg">{getSelectedQuantity(item.id)}</span>
                              <button
                                onClick={() => updateSelectedQuantity(item.id, getSelectedQuantity(item.id) + 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-medium"
                              >
                                +
                              </button>
                            </div>
                          </div>

                          <button
                            onClick={() => addToCart(item, selectedCategoryData.id)}
                            data-testid={`add-to-cart-${item.id}`}
                            className="w-full py-3 bg-sky-500 text-white rounded-lg hover:bg-sky-600 active:bg-sky-700 font-medium text-base transition-colors duration-200 flex items-center justify-center min-h-[48px]"
                          >
                            <span className="mr-2">🛒</span>
                            Add {getSelectedQuantity(item.id)} to Order
                          </button>
                        </div>

                        {/* Desktop/Tablet Layout - Horizontal */}
                        <div className="hidden sm:flex items-center p-4 space-x-4">
                          {item.image_url && (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${item.image_url}`}
                              alt={item.name}
                              className="w-20 h-20 lg:w-24 lg:h-24 object-cover rounded-lg flex-shrink-0"
                              data-testid={`menu-item-image-${item.id}`}
                            />
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-lg" data-testid={`menu-item-name-${item.id}`}>
                              {item.name}
                            </h3>
                            {item.description && (
                              <p className="text-sm text-gray-600 mt-1 line-clamp-2" data-testid={`menu-item-description-${item.id}`}>
                                {item.description}
                              </p>
                            )}
                            <p className="text-xl font-bold text-blue-600 mt-2" data-testid={`menu-item-price-${item.id}`}>
                              {formatPrice(item.price, menuData?.restaurant?.currency || 'USD')}
                            </p>
                          </div>
                          
                          {/* Quantity Selector and Add Button */}
                          <div className="flex flex-col items-center space-y-2 flex-shrink-0">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => updateSelectedQuantity(item.id, getSelectedQuantity(item.id) - 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-medium"
                              >
                                -
                              </button>
                              <span className="w-12 text-center font-medium text-lg">{getSelectedQuantity(item.id)}</span>
                              <button
                                onClick={() => updateSelectedQuantity(item.id, getSelectedQuantity(item.id) + 1)}
                                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center text-gray-600 font-medium"
                              >
                                +
                              </button>
                            </div>
                            <button
                              onClick={() => addToCart(item, selectedCategoryData.id)}
                              data-testid={`add-to-cart-${item.id}`}
                              className="px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 active:bg-sky-700 font-medium transition-colors duration-200 whitespace-nowrap"
                            >
                              🛒 Add {getSelectedQuantity(item.id)} to Order
                            </button>
                          </div>
                        </div>
                      </div>
                    )))}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Menu Temporarily Unavailable</h2>
            <p className="text-gray-600">
              This restaurant has reached its order capacity for today. Please check back later.
            </p>
          </div>
        )}
      </div>

      {/* Minimum Payment Warning Modal */}
      {minimumPaymentWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">⚠️</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Minimum Payment Amount
                </h3>
                <div className="text-gray-600 text-sm mb-4">
                  {minimumPaymentWarning.message}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">Your Order Total:</span>
                  <span className="font-medium">{formatPrice(minimumPaymentWarning.order_amount, minimumPaymentWarning.currency)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Amount to be Charged:</span>
                  <span className="font-bold text-lg text-red-600">{formatPrice(minimumPaymentWarning.minimum_charge_amount, minimumPaymentWarning.currency)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-2">
                  You will be charged the minimum amount due to payment processor requirements.
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setMinimumPaymentWarning(null);
                    setOrderLoading(false);
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => submitOrder('pay_later')}
                  disabled={orderLoading}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Pay Later Instead
                </button>
                <button
                  onClick={proceedWithMinimumPayment}
                  disabled={orderLoading}
                  className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {orderLoading ? (
                    <div className="spinner mx-auto"></div>
                  ) : (
                    'Proceed with Payment'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cart Modal */}
      {/* Cart Modal - Mobile-First Responsive */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-2xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-2xl" data-testid="cart-modal">
            {/* Header */}
            <div className="p-4 sm:p-6 bg-gradient-to-r from-blue-50 to-white border-b border-gray-200 sticky top-0">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900 flex items-center">
                    <span className="mr-2">🛒</span>
                    Your Order
                  </h2>
                  <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                    {cart.reduce((total, item) => total + item.quantity, 0)} items
                  </p>
                </div>
                <button
                  onClick={() => setShowCart(false)}
                  data-testid="close-cart-button"
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-2 rounded-full hover:bg-gray-100 text-xl"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Cart Items - Scrollable */}
            <div className="p-4 sm:p-6 space-y-4 max-h-[50vh] sm:max-h-[40vh] overflow-y-auto">
              {cart.map((item) => (
                <div key={item.menu_item_id} className="bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200" data-testid={`cart-item-${item.menu_item_id}`}>
                  {/* Mobile Layout */}
                  <div className="sm:hidden space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0 pr-2">
                        <h3 className="font-semibold text-gray-900 text-sm leading-tight" data-testid={`cart-item-name-${item.menu_item_id}`}>
                          {item.menu_item_name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-0.5">
                          {formatPrice(item.price, menuData?.restaurant?.currency || 'USD')} each
                        </p>
                      </div>
                      <p className="font-bold text-blue-600 text-sm" data-testid={`cart-item-subtotal-${item.menu_item_id}`}>
                        {formatPrice(item.price * item.quantity, menuData?.restaurant?.currency || 'USD')}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 bg-white rounded-lg border border-gray-300 p-1">
                        <button
                          onClick={() => updateCartItemQuantity(item.menu_item_id, item.quantity - 1)}
                          data-testid={`decrease-quantity-${item.menu_item_id}`}
                          className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center hover:bg-gray-200 active:bg-gray-300 transition-colors duration-200 font-bold text-gray-700"
                        >
                          −
                        </button>
                        <span className="w-8 text-center font-bold text-gray-900" data-testid={`cart-item-quantity-${item.menu_item_id}`}>
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateCartItemQuantity(item.menu_item_id, item.quantity + 1)}
                          data-testid={`increase-quantity-${item.menu_item_id}`}
                          className="w-8 h-8 bg-gray-100 rounded-md flex items-center justify-center hover:bg-gray-200 active:bg-gray-300 transition-colors duration-200 font-bold text-gray-700"
                        >
                          +
                        </button>
                      </div>
                      
                      <button
                        onClick={() => removeFromCart(item.menu_item_id)}
                        data-testid={`remove-from-cart-${item.menu_item_id}`}
                        className="text-red-600 hover:text-red-800 text-sm font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors duration-200"
                      >
                        🗑️ Remove
                      </button>
                    </div>
                    
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Add special notes..."
                        value={item.notes}
                        onChange={(e) => updateCartItemNotes(item.menu_item_id, e.target.value)}
                        data-testid={`cart-item-notes-${item.menu_item_id}`}
                        maxLength={70}
                        className="w-full text-sm px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                        {item.notes.length}/70
                      </div>
                    </div>
                  </div>

                  {/* Desktop Layout */}
                  <div className="hidden sm:flex justify-between items-start space-x-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900" data-testid={`cart-item-name-${item.menu_item_id}`}>
                        {item.menu_item_name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-0.5">
                        {formatPrice(item.price, menuData?.restaurant?.currency || 'USD')} each
                      </p>
                      <div className="relative mt-2">
                        <input
                          type="text"
                          placeholder="Special notes..."
                          value={item.notes}
                          onChange={(e) => updateCartItemNotes(item.menu_item_id, e.target.value)}
                          data-testid={`cart-item-notes-${item.menu_item_id}`}
                          maxLength={70}
                          className="w-full text-sm px-3 py-2 pr-16 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
                          {item.notes.length}/70
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateCartItemQuantity(item.menu_item_id, item.quantity - 1)}
                        data-testid={`decrease-quantity-${item.menu_item_id}`}
                        className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors duration-200 font-bold"
                      >
                        −
                      </button>
                      <span className="w-8 text-center font-bold" data-testid={`cart-item-quantity-${item.menu_item_id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateCartItemQuantity(item.menu_item_id, item.quantity + 1)}
                        data-testid={`increase-quantity-${item.menu_item_id}`}
                        className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors duration-200 font-bold"
                      >
                        +
                      </button>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg text-blue-600" data-testid={`cart-item-subtotal-${item.menu_item_id}`}>
                        {formatPrice(item.price * item.quantity, menuData?.restaurant?.currency || 'USD')}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.menu_item_id)}
                        data-testid={`remove-from-cart-${item.menu_item_id}`}
                        className="text-sm text-red-600 hover:text-red-800 mt-1"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}

              <div className="border-t border-gray-200 pt-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span>Subtotal:</span>
                    <span data-testid="cart-subtotal">{formatPrice(getTotalAmount(), menuData?.restaurant?.currency || 'USD')}</span>
                  </div>
                  
                  {(() => {
                    const taxData = calculateTaxes(getTotalAmount());
                    return taxData.taxes.length > 0 && (
                      <>
                        {taxData.taxes.map((tax, index) => (
                          <div key={index} className="flex justify-between items-center text-sm text-gray-600">
                            <span>
                              {tax.name} {tax.type === 'percentage' ? `(${tax.value}%)` : ''}:
                            </span>
                            <span>{formatPrice(tax.amount, menuData?.restaurant?.currency || 'USD')}</span>
                          </div>
                        ))}
                        {taxData.totalTax > 0 && (
                          <div className="flex justify-between items-center text-sm text-gray-600 border-t pt-2 mt-1">
                            <span className="font-medium">Total Tax:</span>
                            <span className="font-medium">{formatPrice(taxData.totalTax, menuData?.restaurant?.currency || 'USD')}</span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                  
                  <div className="flex justify-between items-center text-lg font-semibold border-t pt-2">
                    <span>Total:</span>
                    <span data-testid="cart-total">{formatPrice(getFinalTotal(), menuData?.restaurant?.currency || 'USD')}</span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => {
                  const finalTotal = getFinalTotal();
                  if (finalTotal < 0) {
                    // For negative totals, skip checkout and place order directly
                    console.log('🎯 Negative total - skipping checkout, placing order directly');
                    if (customerInfo.name && customerInfo.phone) {
                      submitOrder('pay_later');
                    } else {
                      // Still need customer details even for negative totals
                      setShowCheckout(true);
                    }
                  } else {
                    // Normal checkout flow for positive totals
                    setShowCheckout(true);
                  }
                }}
                disabled={cart.length === 0}
                data-testid="proceed-to-checkout-button"
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {getFinalTotal() < 0 ? 'Place Order (No Payment Required)' : 'Proceed to Checkout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full sm:max-w-md sm:rounded-lg rounded-t-lg max-h-[90vh] flex flex-col" data-testid="checkout-modal">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Order Details</h2>
                <button
                  onClick={() => setShowCheckout(false)}
                  data-testid="close-checkout-button"
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Scrollable Content Area */}
            <div className="overflow-y-auto flex-1 overscroll-contain">
              <div className="p-6 space-y-4">
                <div>
                <label htmlFor="customer_name" className="form-label">
                  Name (Optional)
                </label>
                <input
                  type="text"
                  id="customer_name"
                  name="name"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                  data-testid="customer-name-input"
                  className="form-input"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="customer_phone" className="form-label">
                  Phone (Optional)
                </label>
                <input
                  type="tel"
                  id="customer_phone"
                  name="phone"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                  data-testid="customer-phone-input"
                  className="form-input"
                  placeholder="Your phone number"
                />
              </div>

              <div>
                <label htmlFor="customer_email" className="form-label">
                  Email (Optional)
                </label>
                <input
                  type="email"
                  id="customer_email"
                  name="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                  data-testid="customer-email-input"
                  className="form-input"
                  placeholder="Your email address"
                />
              </div>

              <div>
                <label htmlFor="customer_notes" className="form-label">
                  Special Notes (Optional)
                </label>
                <div className="relative">
                  <textarea
                    id="customer_notes"
                    name="notes"
                    value={customerInfo.notes}
                    onChange={handleCustomerInfoChange}
                    onFocus={(e) => setTimeout(() => e.target.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)}
                    data-testid="customer-notes-input"
                    className="form-textarea pr-16"
                    rows={3}
                    placeholder="Any special requests..."
                    maxLength={70}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400 pointer-events-none">
                    {customerInfo.notes.length}/70
                  </div>
                </div>
              </div>
              </div>
            </div>

            {/* Order Summary & Checkout - Fixed Bottom on Mobile */}
            <div className="bg-white border-t border-gray-200 p-4 sm:p-6 sticky bottom-0 sm:relative">
              <div className="mb-4">
                {/* Total Amount Only */}
                <div className="flex justify-between items-center text-xl sm:text-2xl font-bold text-blue-600 py-2">
                  <span>Total Amount:</span>
                  <span data-testid="checkout-total">{formatPrice(getFinalTotal(), menuData?.restaurant?.currency || 'USD')}</span>
                </div>
              </div>

              <div className="space-y-3">
                {/* Special handling for negative totals - no payment required */}
                {getFinalTotal() < 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <div className="text-green-800 font-medium mb-2">
                      🎉 No Payment Required!
                    </div>
                    <div className="text-green-700 text-sm mb-3">
                      Your order total is negative, so no payment is needed.
                    </div>
                    <button
                      onClick={() => submitOrder('pay_later')}
                      disabled={orderLoading}
                      className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 disabled:opacity-50 font-bold"
                    >
                      {orderLoading ? (
                        <div className="spinner mx-auto"></div>
                      ) : (
                        '✅ Place Order Now'
                      )}
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Show payment buttons based on restaurant payment mode */}
                    {(menuData.restaurant.payment_mode === 'payment_only' || menuData.restaurant.payment_mode === 'manual_and_payment') && (
                  <button
                    onClick={() => submitOrder('stripe')}
                    disabled={orderLoading}
                    data-testid="pay-now-button"
                    className="w-full bg-green-600 text-white py-4 sm:py-3 rounded-xl hover:bg-green-700 active:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-base sm:text-lg transition-colors duration-200 min-h-[56px] flex items-center justify-center shadow-lg"
                  >
                    {orderLoading ? (
                      <div className="spinner mx-auto"></div>
                    ) : (
                      <>
                        <span className="mr-2">💳</span>
                        Pay Now with Card
                      </>
                    )}
                  </button>
                )}
                
                {(menuData.restaurant.payment_mode === 'manual_only' || menuData.restaurant.payment_mode === 'manual_and_payment') && (
                  <button
                    onClick={() => submitOrder('pay_later')}
                    disabled={orderLoading}
                    data-testid="pay-later-button"
                    className="w-full bg-gray-700 text-white py-4 sm:py-3 rounded-xl hover:bg-gray-800 active:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-base sm:text-lg transition-colors duration-200 min-h-[56px] flex items-center justify-center shadow-lg"
                  >
                    {orderLoading ? (
                      <div className="spinner mx-auto"></div>
                    ) : (
                      <>
                        <span className="mr-2">📝</span>
                        Order & Pay Later
                      </>
                    )}
                  </button>
                )}
                
                {/* Show message if no payment options are available */}
                {!menuData.restaurant.payment_mode && (
                  <div className="text-center text-gray-500 py-6 bg-gray-50 rounded-xl">
                    <p className="flex items-center justify-center">
                      <span className="mr-2">⏳</span>
                      Payment options are being configured. Please try again later.
                    </p>
                  </div>
                )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Payment Modal */}
      <CustomerPaymentModal
        isOpen={paymentModal.show}
        onClose={() => setPaymentModal({ show: false, clientSecret: null, orderDetails: null, paymentIntentId: null })}
        clientSecret={paymentModal.clientSecret}
        orderDetails={paymentModal.orderDetails}
        paymentIntentId={paymentModal.paymentIntentId}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentCancel={handlePaymentCancel}
        apiClient={apiClient}
        restaurantId={restaurantId}
        customerInfo={customerInfo}
      />
    </div>
  );
};

export default CustomerMenu;